import { useIdentityUsersPasswordChangeMutation } from '../api/repositories/use-identity-users-password-change.mutation';
import { useAuth } from '../contexts/auth';
import { getRoleFromToken, getUserPermissions } from '../utils/token';
import { yupResolver } from '@hookform/resolvers/yup';
import { ApiError, TwoFactorType, UserService } from 'api/generated';
import clsx from 'clsx';
import { PasswordMeterComponent } from 'components/layout/initializers/password-meter-component';
import { Role } from 'features/shared/model';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

type FormValues = {
  oldPassword: string;
  password: string;
  changePassword: string;
};

type TwoFactorState = {
  userId: number;
  twoFactorType: TwoFactorType;
  userEmail?: string;
  userPhone?: string;
  from: string;
};

const loginSchema = yup.object().shape({
  oldPassword: yup
    .string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Old password is required'),
  password: yup.string().min(8, 'Minimum 8 symbols').max(50, 'Maximum 50 symbols').required('Password is required'),
  changePassword: yup.string().when('password', {
    is: (value: string) => (value && value.length > 0 ? true : false),
    then: (schema) => schema.oneOf([yup.ref('password')], "Password and Confirm Password didn't match")
  })
});

const defaultValues: FormValues = {
  oldPassword: '',
  password: '',
  changePassword: ''
};

export function PasswordChange() {
  const location = useLocation();
  const navigate = useNavigate();
  const [initState, setInitState] = useState<TwoFactorState>();
  const [score, setScore] = useState<number>(0);
  const { saveAuth, setCurrentUser, setCurrentPermissions } = useAuth();
  const [formStatus, setFormStatus] = useState('');
  const { mutateAsync: changePassword, isLoading: isSubmitting } = useIdentityUsersPasswordChangeMutation();

  useEffect(() => {
    setInitState(location?.state);
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, isLoading: loading, errors, touchedFields }
  } = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(loginSchema),
    mode: 'onBlur'
  });

  useEffect(() => {
    PasswordMeterComponent.bootstrap();
  });

  useEffect(() => {
    const element = document.body.querySelectorAll('[data-kt-password-meter]')[0] as HTMLElement;
    if (element) {
      const meter = PasswordMeterComponent.getInstance(element);
      setScore(meter?.getScore() ?? 0);
    }
  }, [watch('password')]);

  const onSubmit: SubmitHandler<FormValues> = (values: FormValues) => {
    changePassword(
      {
        input: {
          userId: initState?.userId ?? 0,
          requestBody: {
            currentPassword: values.oldPassword,
            newPassword: values.password
          }
        }
      },
      {
        onSuccess: (auth) => {
          if (auth.token) {
            const role: Role | null = getRoleFromToken(auth.token);
            saveAuth(auth);
            setCurrentUser(auth);
            UserService.getUsersPermissions(role as number).then((permissions) => {
              setCurrentPermissions(getUserPermissions(permissions));
            });
            navigate(`${initState?.from}`);
          } else {
            navigate('/error/500');
          }
        },
        onError: (error) => {
          saveAuth(undefined);
          let errorMessage = 'The login detail is incorrect';
          if (error instanceof ApiError) {
            if (error.body.error.message) errorMessage = error.body.error.message;
          }
          setFormStatus(errorMessage);
          console.error(error);
        }
      }
    );
  };

  const showPassword = (elName: string) => {
    const x = document.getElementsByName(elName)[0] as HTMLInputElement;
    if (x.type === 'password') {
      x.type = 'text';
    }
  };

  const hidePassword = (elName: string) => {
    const x = document.getElementsByName(elName)[0] as HTMLInputElement;
    if (x.type === 'text') {
      x.type = 'password';
    }
  };

  if (!initState) return null;

  return (
    <form
      className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
      noValidate
      id="kt_login_change_password_form"
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* begin::Heading */}
      <div className="text-center mb-11">
        {/* begin::Title */}
        <h1 className="text-dark fw-bolder mb-3">Password Expired</h1>
        <div className="fs-5 mt-6">
          <p className="mb-1">Your password has expired and must be changed.</p>
        </div>
        {/* end::Title */}
      </div>
      {/* end::Heading */}

      {formStatus && (
        <div className="mb-lg-15 alert alert-danger">
          <div className="alert-text font-weight-bold">{formStatus}</div>
        </div>
      )}

      {/* begin::Form group Confirm password */}

      <div className="fv-row mb-5">
        <label className="form-label fw-bolder text-dark fs-6">Current Password</label>
        <div className="input-group input-group-solid">
          <span className="input-group-text cursor-pointer">
            <i
              className="bi bi-eye-fill fs-2"
              onMouseDown={() => {
                showPassword('oldPassword');
              }}
              onMouseUp={() => {
                hidePassword('oldPassword');
              }}
              onMouseLeave={() => {
                hidePassword('oldPassword');
              }}
            >
              <span className="path1"></span>
              <span className="path2"></span>
            </i>
          </span>
          <input
            type="password"
            placeholder="Current password"
            autoComplete="off"
            {...register('oldPassword')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': touchedFields.oldPassword && errors.oldPassword
              },
              {
                'is-valid': touchedFields.oldPassword && !errors.oldPassword
              }
            )}
          />
        </div>
        {touchedFields.oldPassword && errors.oldPassword && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert" className="text-danger">
                {errors.oldPassword.message}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* end::Form group */}

      {/* begin::Form group Password */}
      <div className="fv-row mb-8" data-kt-password-meter="true">
        <div className="mb-1">
          <label className="form-label fw-bolder text-dark fs-6">New Password</label>
          <div className="position-relative mb-3">
            <div className="input-group input-group-solid">
              <span className="input-group-text cursor-pointer">
                <i
                  className="bi bi-eye-fill fs-2"
                  onMouseDown={() => {
                    showPassword('password');
                  }}
                  onMouseUp={() => {
                    hidePassword('password');
                  }}
                  onMouseLeave={() => {
                    hidePassword('password');
                  }}
                >
                  <span className="path1"></span>
                  <span className="path2"></span>
                </i>
              </span>
              <input
                type="password"
                placeholder="Password"
                autoComplete="off"
                {...register('password')}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': touchedFields.password && (errors.password || score !== 100)
                  },
                  {
                    'is-valid': score === 100
                  }
                )}
              />
            </div>
            {touchedFields.password && errors.password && (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">
                  <span role="alert" className="text-danger">
                    {errors.password.message}
                  </span>
                </div>
              </div>
            )}
          </div>
          {/* begin::Meter */}
          <div className="d-flex align-items-center mb-3" data-kt-password-meter-control="highlight">
            <div className="flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2"></div>
            <div className="flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2"></div>
            <div className="flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2"></div>
            <div className="flex-grow-1 bg-secondary bg-active-success rounded h-5px"></div>
          </div>
          {/* end::Meter */}
        </div>
        <div className="text-muted">Use 8 or more characters with a mix of letters, numbers & symbols.</div>
      </div>
      {/* end::Form group */}

      {/* begin::Form group Confirm password */}
      <div className="fv-row mb-5">
        <label className="form-label fw-bolder text-dark fs-6">Repeat New Password</label>
        <input
          type="password"
          placeholder="Password confirmation"
          autoComplete="off"
          {...register('changePassword')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': touchedFields.changePassword && errors.changePassword
            },
            {
              'is-valid': touchedFields.changePassword && !errors.changePassword
            }
          )}
        />
        {touchedFields.changePassword && errors.changePassword && (
          <div className="fv-plugins-message-container">
            <div className="fv-help-block">
              <span role="alert">{errors.changePassword.message}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className="text-center">
        <button
          type="submit"
          id="kt_sign_up_submit"
          className="btn btn-lg btn-primary w-100 mb-5"
          disabled={isSubmitting || !isValid || score !== 100}
        >
          {!loading && !isSubmitting && <span className="indicator-label">Change Password</span>}
          {(loading || isSubmitting) && (
            <span className="indicator-progress" style={{ display: 'block' }}>
              Please wait... <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Form group */}
    </form>
  );
}
